/**
 * SupportAccessBanner — shown on EVERY page of the ACCOUNT OWNER while a
 * sendSMS support (impersonation) session is live on their account (2b-2,
 * PO 03-Sep). The symmetric twin of ImpersonationBanner: the admin sees
 * the loud amber banner on the impersonated view; the owner now sees this
 * calm blue one on their own.
 *
 * Data source: useAuth() -> /me -> support_access_active. Zero new fetch;
 * the flag is computed server-side only for normal sessions, so this and
 * ImpersonationBanner can never render together (double-guarded here).
 *
 * DISPLAY-ONLY: transparency, not control. "Manage" links to Settings >
 * Security where the existing consent revoke lives (session-scoped
 * server-side, V482). Enforcement never depends on this banner.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Eye } from "lucide-react";

import { useAuth } from "@/lib/auth/context";

export default function SupportAccessBanner() {
  const { t } = useTranslation();
  const { supportAccessActive, impersonation } = useAuth();

  // Never alongside the admin's own impersonation banner.
  if (!supportAccessActive || impersonation) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-sky-500/40 bg-sky-500 px-4 py-2.5 text-center text-[13px] font-semibold text-sky-950 shadow-sm animate-fade-in sm:px-6"
    >
      <Eye className="h-4 w-4 shrink-0 stroke-[2]" aria-hidden="true" />
      <span className="hidden sm:inline">{t("support_banner.label")}</span>
      <span className="sm:hidden">{t("support_banner.label_short")}</span>
      <Link
        to="/settings"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sky-950 px-3 py-1 text-[12px] font-semibold text-sky-50 transition-colors hover:bg-sky-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-950/40"
      >
        {t("support_banner.manage")}
      </Link>
    </div>
  );
}
